import React, { useState } from 'react'
import UserAvatar from './UserAvatar'
import { useNewConversation } from '../../hooks/useNewConversation'

interface NewConversationFormProps {
  onCancel: () => void
  onSuccess: (conversationId: number) => void
  searchQuery: string
  onSearchQueryChange: (query: string) => void
  searchResults: any[]
}

const NewConversationForm = ({
  onCancel,
  onSuccess,
  onSearchQueryChange,
  searchResults
}: NewConversationFormProps) => {
  const [recipientIdentifier, setRecipientIdentifier] = useState('')
  const [message, setMessage] = useState('')
  const [selectedUser, setSelectedUser] = useState<any>(null)

  const { createNewConversation, error, loading } = useNewConversation()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const finalRecipient = selectedUser ? selectedUser.id.toString() : recipientIdentifier.trim()
    if (!finalRecipient || !message.trim()) return

    const conversationId = await createNewConversation(finalRecipient, message)
    if (conversationId) {
      onSuccess(conversationId)
      setRecipientIdentifier('')
      setMessage('')
      setSelectedUser(null)
    }
  }

  const handleUserSelect = (user: any) => {
    setRecipientIdentifier(user.username)
    setSelectedUser(user)
    onSearchQueryChange('')
  }

  return (
    <div
      className='tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-5 md:tw-p-6'
    >
      <h5 className='tw-text-lg tw-font-semibold tw-text-gray-800 tw-mb-5 md:tw-mb-6'>
        Nowa konwersacja
      </h5>
      <form onSubmit={handleSubmit} className='tw-space-y-4 md:tw-space-y-5'>
        <div className='tw-relative'>
          {selectedUser
            ? (
              <div
                className='tw-bg-slate-100 tw-border tw-border-slate-300 tw-rounded-md tw-p-2.5 tw-flex tw-items-center tw-justify-between'
              >
                <div className='tw-flex tw-items-center tw-min-w-0'>
                  <UserAvatar name={selectedUser.username} size={32} id={selectedUser.id} />
                  <div className='tw-ml-2.5 tw-flex-1 tw-min-w-0'>
                    <p className='tw-text-sm tw-font-medium tw-text-slate-800 tw-truncate'>{selectedUser.username}</p>
                    {selectedUser.email && <small className='tw-text-xs tw-text-slate-500 tw-truncate'>{selectedUser.email}</small>}
                  </div>
                </div>
                <button
                  type='button'
                  className='tw-ml-2 tw-flex-shrink-0 tw-p-1 tw-text-red-500 hover:tw-text-red-700 hover:tw-bg-red-100 tw-rounded-full focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-red-500'
                  onClick={() => {
                    setSelectedUser(null)
                    setRecipientIdentifier('')
                  }}
                  aria-label='Usuń wybranego użytkownika'
                >
                  <i className='fa fa-times' />
                </button>
              </div>
              )
            : (
              <input
                type='text'
                className='tw-w-full tw-px-3.5 tw-py-2.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-sky-500 focus:tw-border-sky-500 tw-placeholder-gray-400 transition-colors'
                placeholder='Login, email lub ID odbiorcy'
                value={recipientIdentifier}
                onChange={(e) => {
                  const query = e.target.value
                  setRecipientIdentifier(query)
                  if (query.length >= 3) {
                    onSearchQueryChange(query)
                  } else {
                    onSearchQueryChange('')
                  }
                }}
                required
              />
              )}

          {/* Wyniki wyszukiwania */}
          {!selectedUser && searchResults && searchResults.length > 0 && (
            <div
              className='tw-absolute tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md tw-shadow-lg tw-max-h-60 tw-overflow-y-auto tw-z-20'
            >
              {searchResults.map(user => (
                <div
                  key={user?.id}
                  className='tw-p-3 hover:tw-bg-slate-50 tw-cursor-pointer tw-transition-colors tw-duration-150 tw-border-b tw-border-gray-100 last:tw-border-b-0'
                  onClick={() => handleUserSelect(user)}
                >
                  <div className='tw-flex tw-items-center'>
                    <UserAvatar name={user?.username} size={32} id={user?.id} />
                    <div className='tw-ml-2.5 tw-min-w-0'>
                      <p className='tw-text-sm tw-font-medium tw-text-slate-700 tw-truncate'>{user?.username}</p>
                      {user?.email && <small className='tw-text-xs tw-text-slate-500 tw-truncate'>{user?.email}</small>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pole Wiadomości */}
        <div>
          <textarea
            className='tw-w-full tw-px-3.5 tw-py-2.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-sky-500 focus:tw-border-sky-500 tw-placeholder-gray-400 transition-colors'
            placeholder='Treść wiadomości'
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
          />
        </div>

        {error && (
          <div className='tw-bg-red-50 tw-border-l-4 tw-border-red-500 tw-text-red-700 tw-p-3 tw-rounded-md tw-text-sm'>
            {error}
          </div>
        )}

        <div className='tw-flex tw-flex-col sm:tw-flex-row-reverse sm:tw-justify-start tw-gap-3'>
          <button
            type='submit'
            className='tw-w-full sm:tw-w-auto tw-inline-flex tw-justify-center tw-items-center tw-px-6 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-rounded-md tw-transition-colors tw-duration-150 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed tw-bg-[#86B97C] hover:tw-bg-[#70A864] focus:tw-ring-[#86B97C]'
            disabled={loading || (!selectedUser && !recipientIdentifier.trim()) || !message.trim()}
          >
            {loading
              ? (
                <>
                  <svg className='tw-animate-spin tw-mr-2 tw-h-4 tw-w-4 tw-text-white' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24'>
                    <circle className='tw-opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                    <path className='tw-opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z' />
                  </svg>
                  Wysyłanie...
                </>
                )
              : (
                  'Wyślij'
                )}
          </button>
          <button
            type='button'
            className='tw-w-full sm:tw-w-auto tw-inline-flex tw-justify-center tw-items-center tw-px-6 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-rounded-md tw-transition-colors tw-duration-150 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 disabled:tw-opacity-60 tw-bg-[#6C757D] hover:tw-bg-[#5a6268] focus:tw-ring-[#6C757D]'
            onClick={onCancel}
            disabled={loading}
          >
            Anuluj
          </button>
        </div>
      </form>
    </div>
  )
}

export default NewConversationForm
